<template>
  <div class="w-full pt-5 pb-5">
    <div class="flex items-center w-full px-4 bg-white">
      <div class="flex items-center space-x-2">
        <back-button label="Back" @onClick="goBack" variant="secondary" class="transition-colors duration-200" />

        <div class="h-8 border-l border-gray-300"></div>

        <h1 class="text-xl font-bold text-gray-800">New Agreement</h1>
      </div>

      <div class="hidden md:block ml-2">
        <Breadcrumb :items="breadcrumbs" class="px-3 py-1 rounded-lg" />
      </div>
    </div>

    <template v-if="loadingTemplates">
      <div class="mt-20 h-full">
        <Loader size="xxs" :loader-image="false" />
      </div>
    </template>


    <ValidationObserver v-slot="{ handleSubmit }" v-else>
      <!-- Agreement Details Section -->
      <div class="px-3">
        <card class="p-5 mt-3">
          <p class="text-lg font-bold">New Agreement</p>
          <div class="grid grid-cols-12 gap-7 mt-4">
            <!-- Financial Year -->
            <div class="col-span-4">
              <div class="text-sm text-gray-600 mb-1">Financial Year</div>
              <div class="date-flex mt-2" :class="{ 'border-red-500': periodError }">
                <datepicker placeholder="--Select Year--" class="w-full" input-class="date-input" format="yyyy"
                  minimum-view="year" @selected="periodSelect($event)" />
              </div>
              <small v-if="periodError" class="text-red-700 text-xs font-semibold">
                Financial year is not valid.
              </small>
            </div>

            <!-- Description -->
            <div class="col-span-4">
              <c-text label="Description" placeholder="--Enter--" variant="w-full" class="mt-2"
                v-model="payload.description" :disabled="true" :rules="['required']" />
            </div>

            <!-- Template Selection -->
            <div class="col-span-4">
              <c-select label="Template" placeholder="Select Template" variant="w-full" class="mt-2"
                :options="filteredTemplateOptions.map(({ year, ...rest }) => rest)" v-model="selectedTemplate"
                :rules="['required']" :disabled="disableTemplate" />
            </div>
          </div>




          <!-- Cycle Details Section -->

          <template v-if="selectedTemplate">
            <div class="border border-dashed bg-white border-romanSilver rounded-md gap-4 p-3 mt-4">
              <div class="flex items-center w-full gap-2 cursor-pointer">
                <span class="font-black text-xs leading-5 uppercase tracking-wider text-romanSilver whitespace-nowrap">
                  Cycle Details
                </span>

                <div class="flex-grow border-t border-solid border-romanSilver h-0"></div>

                <Icon :icon-name="'chevronDown'" class-name="text-darkPurple self-center" size="xms" />
              </div>

              <div class="grid gap-2 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 mt-3">
                <div class="flex w-full p-3 shadow rounded-md gap-2">
                  <Icon size="s" icon-name="doc-cycle" class-name="text-romanSilver self-start pt-1" />
                  <div class="flex flex-col">
                    <span class="font-black text-xs leading-5 tracking-wide text-darkPurple uppercase">
                      {{ cycleTitle }}
                    </span>
                    <span class="font-bold text-sm leading-6 tracking-wide text-jet uppercase">
                      {{ cycleDescription }}
                    </span>
                  </div>
                </div>

                <div class="flex w-full p-3 shadow rounded-md gap-2">
                  <Icon size="xs" icon-name="calendar" class-name="text-romanSilver self-start pt-1" />
                  <div class="flex flex-col">
                    <span class="font-black text-xs leading-5 tracking-wide text-carrotOrange uppercase">
                      Period
                    </span>
                    <span class="font-bold text-sm leading-6 tracking-wide text-jet uppercase">
                      {{ $DATEFORMAT(new Date(cycleStartPeriod), "MMMM dd, yyy") }} -
                      {{ $DATEFORMAT(new Date(cycleEndPeriod), "MMMM dd, yyy") }}
                    </span>
                  </div>
                </div>

                <div v-for="(cycle, index) in appraisalCycles" :key="index"
                  class="flex w-full p-3 shadow rounded-md gap-2">
                  <Icon size="xs" icon-name="reviewicon" class-name="text-romanSilver self-start pt-1" />
                  <div class="flex flex-col">
                    <span class="font-black text-xs leading-5 tracking-wide text-blueCrayola uppercase">
                      {{ cycle.name }}
                    </span>
                    <span class="font-bold text-sm flex leading-6 tracking-wide text-jet uppercase">
                      {{ $DATEFORMAT(new Date(cycle.appraisal_starts), "MMMM dd, yyy") }} -
                      {{ $DATEFORMAT(new Date(cycle.appraisal_ends), "MMMM dd, yyy") }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="w-full p-2 mt-5 border border-dashed border-gray-400 rounded">
              <div class="text-xs font-bold text-gray-500 uppercase">PERFORMANCE DETAILS</div>
              <div class="text-sm text-gray-500 text-center py-4">
                Select a Performance Template to view Appraisal Performance Details
              </div>
            </div>
          </template>
        </card>
      </div>

      <!-- KPAs Section -->
      <div class="px-3 mt-4">
        <card class="p-5">
          <div class="flex items-center justify-between mb-4">
            <h1 class="text-base font-bold">KPAs</h1>

            <div class="flex items-center gap-2">
              <!-- Weight Summary -->
              <div class="p-2 bg-gray-100 rounded-md flex items-center">
                <span class="text-sm font-bold mr-2">Total Allocated Weight:</span>
                <span class="text-sm text-darkPurple mr-1"
                  :class="allocatedWeight === 100 ? 'text-darkPurple' : 'text-desire'">{{ allocatedWeight }}%</span>
                <icon :icon-name="iconInfo" size="xs" />
              </div>

              <div class="h-8 w-px bg-gray-400 mx-2" />

              <!-- Import Actions -->
              <button class="flex items-center gap-1 text-blue-500" @click="importGoalModal = true">
                <Icon icon-name="import" style="color: white; fill: #FFFFFF" size="xsm" />
                <span>Import from Previous Agreement</span>
              </button>

              <div class="h-8 w-px bg-gray-400 mx-2" />

              <div class="w-auto flex cursor-pointer gap-1 items-center" @click="impExpOptSelected()">
                <Icon icon-name="import" style="color: white; fill: #FFFFFF" size="xsm" />
                <span class="text-blueCrayola text-base">Import from File</span>
              </div>
            </div>
          </div>

          <div class="border-b border-gray-400 my-2" />

          <!-- Empty State -->
          <template v-if="!selectedTemplate || templateKPATypes.length === 0">
            <div class="flex flex-col w-full items-center py-8">
              <icon :icon-name="illustration" size="xl" :icon-style="'width : 12rem'" class="mb-4" />
              <p class="text-center text-gray-600 max-w-lg">
                You set organisational goals and objectives here. KPAs appear when you select a template.
                You can edit selected template based on permissions assigned. To get started select a
                template or create new in performance settings.
              </p>
            </div>
          </template>

          <!-- KPA List -->
          <template v-else>
            <div class="w-full">
              <accordion v-for="(kpaTypes, index) in templateKPATypes" :key="`kpa-${index}`" class="mt-4" show-divider
                :is-open="true" @open="handleOpen">
                <template v-slot:title>
                  <ValidationObserver :ref="`kpa-observer-${kpaTypes.id}`" v-slot="{ handleSubmit }">
                    <div class="flex gap-3">
                      <span class="font-bold">{{ kpaTypes.name }}</span>
                      <span class="flex cursor-pointer" @click="handleSubmit(() => createNewKPA(kpaTypes))">
                        <icon icon-name="icon-plus" size="xs" />
                      </span>
                      <span class="flex cursor-pointer text-blueCrayola" @click="toggleEditMode(kpaTypes)">
                        <icon :icon-name="isKpaEditable(kpaTypes) ? 'icon-save' : 'icon-edit'" size="xs" />
                      </span>
                    </div>
                  </ValidationObserver>
                </template>

                <template v-slot:block>
                  <div class="flex items-center bg-gray-100 rounded px-2 py-1 gap-1 mr-3">
                    <span class="font-black text-xs"
                      :class="getKPAWeight(kpaTypes, kpaTypes.id) !== getTotalWeight(kpaTypes, kpaTypes.id) ? 'text-desire' : 'text-mediumSeaGreen'">
                      {{ getKPAWeight(kpaTypes, kpaTypes.id) }}%
                    </span>
                    <!-- <span class="font-black text-xs"> of </span>
                    <span class="font-black text-xs"
                      :class="getKPAWeight(kpaTypes, kpaTypes.id) !== getTotalWeight(kpaTypes, kpaTypes.id) ? 'text-darkPurple' : 'text-mediumSeaGreen'">
                      {{ getTotalWeight(kpaTypes, kpaTypes.id) }}%
                    </span> -->
                  </div>
                </template>



                <div class="flex flex-col w-full -mt-2">

                  <!-- Strategic Goal Section -->
                  <div class="w-full mb-6 p-4 bg-alertRed rounded-lg shadow-sm">
                    <h2 class="text-base font-bold text-gray-800 mb-2">STRATEGIC GOAL</h2>
                    <p class="text-gray-600">{{ kpaTypes.description }}</p>
                  </div>

                  <div class="w-full flex flex-col gap-6">
                    <accordion class="px-5 py-3 mb-0 bg-gray-50 shadow-sm rounded-lg" :icon-class="'mt-4'"
                      v-for="(item, index) in kpaTypes.kpas" :key="'kpi-' + index" :is-open="true">
                      <template v-slot:block>
                        <div class="w-full flex flex-col lg:flex-row items-start justify-between gap-4">
                          <div class="flex items-start w-full lg:w-auto flex-1 gap-2">
                            <div class="flex items-center" :class="!item.isEdit ? 'h-7 mt-1' : 'h-8 mt-2'">
                              <button @click="deleteKPA(index, kpaTypes)"
                                class="text-gray-500 hover:text-red-500 transition-colors">
                                <icon icon-name="icon-trash" size="xs" />
                              </button>
                            </div>

                            <div :class="!item.isEdit ? 'h-6 mt-1' : 'h-8 mt-2'" class="w-auto mx-2 flex items-center">
                              <span class="w-px bg-gray-300" :class="!item.isEdit ? 'h-6' : 'h-8'"></span>
                            </div>

                            <div v-if="!item.isEdit" class="flex gap-2 whitespace-nowrap mt-2">
                              <span class="font-bold text-flame text-sm">KPI:</span>
                              <span class="font-semibold text-sm" v-tooltip.bottom-middle="item.name
                                ">{{ item.data?.name || item.name }}</span>
                            </div>

                            <ValidationProvider rules="required" v-slot="{ errors }" v-else
                              class="min-w-[200px] lg:min-w-[250px]">
                              <div class="flex items-center gap-2 w-full">
                                <span class="font-bold mt-1 text-flame text-sm whitespace-nowrap">KPI :</span>
                                <c-text placeholder="--Enter--" variant="w-full" :style="'width: 20rem'"
                                  v-model="item.name" :error="errors[0]" />
                              </div>
                            </ValidationProvider>

                            <div :class="!item.isEdit ? 'h-6 mt-1' : 'h-8 mt-2'" class="w-auto mx-2 flex items-center">
                              <span class="w-px bg-gray-300" :class="!item.isEdit ? 'h-6' : 'h-8'"></span>
                            </div>

                            <!-- Objective Field -->
                            <div v-if="!item.isEdit" class="flex gap-2 whitespace-nowrap mt-2">
                              <span class="font-bold text-flame text-sm">Objective:</span>
                              <span class="font-semibold text-sm" v-tooltip.bottom-middle="item.objective">{{
                                item.data?.objective || item.objective }}</span>
                            </div>

                            <ValidationProvider rules="required" v-slot="{ errors }" v-else
                              class="flex-1 min-w-[200px] lg:min-w-[250px]">
                              <div class="flex items-center gap-2 w-full">
                                <span class="font-bold mt-1 text-flame uppercase text-sm whitespace-nowrap">Objective
                                  :</span>
                                <c-text placeholder="--Enter--" variant="w-full" class="w-[300px]"
                                  :style="'width: 20rem'" v-model="item.objective" :error="errors[0]" />
                              </div>
                            </ValidationProvider>
                          </div>

                          <div class="w-full lg:w-auto lg:ml-8 mt-2 mr-3 lg:mt-0">
                            <div v-if="!item.isEdit" class="p-2 w-auto flex gap-2 items-center rounded-sm mr-2 mt-1">
                              <span class="font-black text-xs">{{ item.data?.weight || item.weight }}%</span>
                            </div>

                            <ValidationProvider rules="required" v-slot="{ errors }" v-else
                              class="flex items-center gap-2">
                              <span class="font-bold mt-1 text-flame uppercase text-sm whitespace-nowrap">Weight
                                :</span>
                              <c-text placeholder="--Enter--" variant="w-full" class="w-14" type="number" min="0"
                                max="100" v-model="item.weight" :error="errors[0]"
                                @input="validateWeight(item.weight)" />
                            </ValidationProvider>
                          </div>
                        </div>
                      </template>
                      <div class="w-full h-[1px] mb-6 -mt-2 border border-2" />

                      <div class="w-full flex flex-col gap-1">
                        <span class="text-sm uppercase font-bold">Criteria</span>
                      </div>
                      <div class="w-full grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                        <ValidationProvider v-for="(n, rating) in (item.data?.kpis || item.kpis)" :key="rating"
                          rules="required" v-slot="{ errors }">
                          <c-text variant="w-full" :placeholder="`Criteria`"
                            v-tooltip.bottom-middle="item.kpis[rating].criteria" :disabled="!item.isEdit"
                            v-model="item.kpis[rating].criteria" :error="errors[0]" />
                        </ValidationProvider>
                      </div>
                    </accordion>
                  </div>
                </div>














                <!----<div class="flex flex-col mt-2">
                  <ValidationProvider rules="required" v-slot="{ errors }">
                    <div class="w-full mb-4 p-4 bg-white rounded-lg shadow">
                      <h3 class="text-base font-bold text-gray-800 mb-2">Strategic Goal</h3>
                      <c-textarea placeholder="--Enter--" v-model="kpaType.strategicGoal" :error="errors[0]" />
                    </div>
                  </ValidationProvider>

                  <div class="space-y-4">
                    <accordion v-for="(kpa, kpaIndex) in kpaType.kpas" :key="`kpi-${kpaIndex}`"
                      class="px-4 bg-gray-50 rounded-lg shadow-sm" :is-open="true">
                      <template v-slot:block>
                        <div class="flex items-center justify-between">
                          <div class="flex items-center">
                            <button v-if="!kpa.isLocked" @click="deleteKPA(kpaIndex, kpaType)"
                              class="mr-2 text-red-500">
                              <icon icon-name="icon-trash" size="xs" />
                            </button>

                            <div class="w-px h-6 bg-gray-300 mx-2" />

                            <ValidationProvider rules="required" v-slot="{ errors }">
                              <div class="flex items-center">
                                <span class="font-bold text-red-500 text-sm mr-2">KPI:</span>
                                <c-text placeholder="--Enter--" class="w-72" :disabled="kpa.isLocked" v-model="kpa.name"
                                  :error="errors[0]" />
                              </div>
                            </ValidationProvider>

                            <div class="w-px h-6 bg-gray-300 mx-2" />

                            <ValidationProvider rules="required" v-slot="{ errors }">
                              <div class="flex items-center">
                                <span class="font-bold text-red-500 text-sm mr-2">Objective:</span>
                                <c-text placeholder="--Enter--" class="w-72" :disabled="kpa.isLocked"
                                  v-model="kpa.objective" :error="errors[0]" />
                              </div>
                            </ValidationProvider>
                          </div>

                          <ValidationProvider rules="required|min_value:0|max_value:100" v-slot="{ errors }">
                            <div class="flex items-center">
                              <span class="font-bold text-red-500 text-sm mr-2">Weight:</span>
                              <c-text placeholder="--Enter--" type="number" min="0" max="100" class="w-20"
                                :disabled="kpa.isLocked" v-model="kpa.weight" :error="errors[0]" />
                            </div>
                          </ValidationProvider>
                        </div>
                      </template>

                      <div class="border-t border-gray-200 my-3" />

                      <div class="mb-2">
                        <span class="text-sm font-bold uppercase">Criteria</span>
                      </div>
                      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <ValidationProvider v-for="(criteria, ratingIndex) in kpa.kpis" :key="ratingIndex"
                          rules="required" v-slot="{ errors }">
                          <c-text :placeholder="`Criteria ${lowestRating + ratingIndex}`" :disabled="kpa.isLocked"
                            v-model="criteria.criteria" :error="errors[0]" />
                        </ValidationProvider>
                      </div>
                    </accordion>
                  </div>
                </div>--->


              </accordion>
            </div>
          </template>
        </card>
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center justify-between px-3 mt-5">
        <div class="flex gap-3">
          <Button :width="'10rem'" class="bg-dynamicBackBtn text-white w-40" options
            :option-list="userReportingTo ? ['Submit', 'Share with manager'] : ['Submit']" @click="
              () => {
                payload.isDraft = false;
                handleSubmit(() => submit('active'))
              }">
            Submit
          </Button>

          <Button variant="outline" @click="cancel">
            Cancel
          </Button>
        </div>

        <Button class="text-dynamicBackBtn border border-solid border-dynamicBackBtn text-center" width="8rem" @click="() => {
          payload.isDraft = false;
          submit('draft')
        }" v-if="payload.status === 'draft' && payload.description !== ''">
          Save as Draft
        </Button>
      </div>
    </ValidationObserver>


    <!-- Import Goal Modal -->
    <ImportGoal :import-goal-modal="importGoalModal" :filtered-template-options="filteredTemplateOptions"
      @close="importGoalModal = false" @import="importTemplate($event)" />


    <!-- Import Modal -->
    <Import :open-modal="openImport" :payload="payload" :selected-template="selectedTemplate" @close="closeImport" />
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Datepicker from "vuejs-datepicker";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import Card from "@/components/Card";
import Icon from "@/components/Icon";
import Button from "@/components/Button";
import Accordion from "@/components/Accordion";
import ImportGoal from "@/components/ImportGoal";
import paramsMixin from "@/utilities/paramsMixin";
import timeFormatMixin from "@/utilities/timeFormatMixin";
import Import from "./import";

export default {
  mixins: [paramsMixin, timeFormatMixin],

  components: {
    ValidationObserver,
    ValidationProvider,
    BackButton,
    Breadcrumb,
    ImportGoal,
    Icon,
    CText,
    Card,
    Button,
    CSelect,
    Accordion,
    Datepicker,
    Import
  },

  data() {
    return {
      breadcrumbs: [
        { id: 'agreement', text: "Performance Agreement", href: "Performance Agreement" },
        { id: 'my-agreements', text: "My Agreements", href: "My Agreements" },
        { id: 'new-agreement', text: "New Agreement" }
      ],
      illustration: "illustration",
      doccycle: "doc-cycle",
      calendarIcon: "calendar",
      reviewIcon: "reviewicon",
      iconInfo: "icon-info",
      loadingTemplates: false,
      disableBtn: true,
      periodError: false,
      openImport: false,
      importGoalModal: false,
      weightAdjust: false,
      functionId: '',
      locationId: '',
      designationId: '',
      levelId: '',
      allocatedWeight: 0,
      lowestRating: 1,
      selectedTemplate: "",
      userReportingTo: null,
      templateOptions: [],
      filteredTemplateOptions: [],
      templateKPATypes: [],
      appraisalCycles: [],
      cycleStartPeriod: "",
      cycleEndPeriod: "",
      cycleTitle: "",
      cycleDescription: "",
      disableTemplate: true,
      params: null,
      payload: {
        orgId: this.$orgId || "",
        templateId: "",
        goalId: "",
        title: "",
        status: "draft",
        isDraft: false,
        created_by: this.$AuthUser?.id || "",
        created_for: this.$AuthUser?.id || "",
        share_for_review: false,
        year: '',
        description: "",
        kpa_types: []
      },
      kpaWeights: [],
      Ratings: [],
      managerLock: false,
      editingLock: false,
      employeeSubmitted: '',
      weightRules: [
        "required",
        {
          name: "weight",
          rule: (w) => this.validateWeight(w),
        },
      ],
      inputRules: [
        "required",
        {
          name: "data",
          rule: (w) => this.validateEntries(w),
        },
      ],
      hasDirectReport: false
    };
  },

  computed: {
    isFormValid() {
      return this.selectedTemplate &&
        this.payload.year &&
        this.allocatedWeight === 100 &&
        this.validateKPAs();
    },
  },

  watch: {
    selectedTemplate(newVal) {
      if (newVal) {
        this.loadTemplateDetails(newVal);
        this.calculateTotalAllocatedWeight();
      }
    }
  },

  async created() {
    await this.loadEmployeeDetails()
  },

  methods: {
    isKpaEditable(kpaTypes) {
      return kpaTypes.kpas.some(kpa => kpa.isEdit);
    },

    importTemplate(value) {
      if (value) {
        this.selectedTemplate = value;
      }
    },

    toggleEditMode(kpaTypes) {
      if (!this.isKpaEditable(kpaTypes)) {
        kpaTypes.kpas.forEach(kpa => {
          this.$set(kpa, 'isEdit', true);
        });
      } else {
        const allKPAsValid = kpaTypes.kpas.every(kpa => {
          const hasRequiredFields = kpa.name && kpa.objective && kpa.weight;

          const allKPIsValid = !kpa.kpis || kpa.kpis.every(kpi => kpi.criteria);

          return hasRequiredFields && allKPIsValid;
        });

        if (allKPAsValid) {
          kpaTypes.kpas.forEach(kpa => {
            this.$set(kpa, 'isEdit', false);
          });
          this.payload.isDraft = true;
          this.submit('draft'); // Uncomment when ready to save
        } else {
          this.$toasted.error('Please fill all required fields before saving', {
            duration: 5000
          });
        }
      }
    },

    validateEntries(t) {
      if (t !== "") {
        return true;
      }

      return false;
    },

    validateWeight(weight) {
      let sum = 0;
      if (weight < 0) {
        return "Value must be greater than or equal to 0";
      }

      this.templateKPATypes.forEach((kpaType) => {
        kpaType.kpas.map((kpi) => {
          sum += Number(kpi.weight);
          return {};
        });
        return {};
      });

      this.allocatedWeight = sum;

      if (sum !== 100) {
        this.disableBtn = true;
      } else {
        this.disableBtn = false;
      }

      return true;
    },

    async impExpOptSelected() {
      await this.handleImport();
    },

    async initializeData() {
      try {
        this.loadingTemplates = true;

        // Load ratings first as they're needed for criteria
        await this.loadPerformanceRatings();

        // Load templates and other data in parallel
        await Promise.all([
          this.loadTemplates(),
          this.loadAppraisalCycles(),
          this.loadPerformanceSettings()
        ]);
      } catch (error) {
        this.$toasted.error(`Failed to initialize data: ${error.message}`);
      } finally {
        this.loadingTemplates = false;
      }
    },

    async loadPerformanceRatings() {
      try {
        const response = await this.$_getPerfomanceRating();
        this.Ratings = response.data.PerformanceRatings.sort((a, b) => a.rating - b.rating);
        this.lowestRating = this.Ratings[0]?.rating || 1;
      } catch (error) {
        throw new Error(error);
      }
    },

    async loadTemplates() {
      try {
        const currentYear = new Date().getFullYear();
        const [templatesResponse, goalsResponse] = await Promise.all([
          this.$_getDesignatedTemplate(this.params),
          this.$_getOrganizationGoal("")
        ]);

        // Process goals to find current year's goal
        const currentYearGoal = goalsResponse.data.goals.find(
          goal => goal.goal.year === currentYear
        );
        if (currentYearGoal) {
          this.payload.goalId = currentYearGoal.goal.id;
        }

        const activeTemplates = templatesResponse.data.PerformanceTemplate.filter(
          t => t.status !== "draft"
        );

        // Now build filteredTemplateOptions
        this.filteredTemplateOptions = activeTemplates
          .map(template => ({
            id: template.id,
            name: template.agreement_title,
            year: template.year
          }));

        // Build templateOptions and include the matched templateId if available
        this.templateOptions = activeTemplates.map(template => ({
          id: template.id,
          name: template.agreement_title,
          data: template,
          matchedTemplateId: template.templateLinkId
        }));

      } catch (error) {
        this.$toasted.error("Failed to load performance templates");
      }
    },

    async loadTemplateDetails(templateId) {
      try {
        const response = await this.$_getOneTemplate(templateId);
        const template = response.data.PerformanceTemplate;

        this.payload.templateId = templateId;
        this.cycleDescription = template.agreement_description;
        this.cycleTitle = template.agreement_title;
        this.payload.description = this.cycleDescription;

        // Find the full template data
        this.processKPATypes(template.kpa_types);

        this.disableBtn = false;
      } catch (error) {
        this.$toasted.error("Failed to load template details");
      }
    },

    processKPATypes(kpaTypes) {
      this.templateKPATypes = kpaTypes.map(kpaType => {
        const processedKPAs = [this.createEmptyKPA()];
        return {
          ...kpaType,
          kpas: processedKPAs
        };
      });
    },

    createEmptyKPA() {
      return {
        state: true,
        id: "",
        isLocked: false,
        isEdit: true,
        name: null,
        objective: null,
        kpis: this.getCriteriaData(),
        target_date: null,
        weight: ""
      };
    },

    getCriteriaData(existingKPIs = null) {
      if (existingKPIs) {
        return this.Ratings.map((rating, index) => ({
          id: existingKPIs[index]?.id,
          performanceRatingId: rating.id,
          criteria: existingKPIs[index]?.criteria || ""
        }));
      }

      return this.Ratings.map(rating => ({
        performanceRatingId: rating.id,
        criteria: ""
      }));
    },

    async loadEmployeeDetails() {
      try {
        const response = await this.$_getOneEmployeeAll({
          id: this.$AuthUser?.id
        });
        this.userReportingTo = response.data.userReportingTo;
        this.designationId = response.data.designationId;
        this.functionId = response.data.functionId;
        this.locationId = response.data.officeId;
        this.levelId = response.data.employeeLevelId;
        this.params = `?locationId=${this.locationId}&designationId=${this.designationId}&functionId=${this.functionId}&levelId=${this.levelId}`;
        this.initializeData();
      } catch (error) {
        throw new Error(error);
      }
    },

    async loadAppraisalCycles() {
      try {
        const response = await this.$_getAppraisalCycle(this.agreementQuery());
        const appraisal = response.data.AppraisalCycle[0];

        if (appraisal) {
          this.cycleStartPeriod = appraisal.cycle_start_date;
          this.cycleEndPeriod = appraisal.cycle_end_date;

          this.appraisalCycles = appraisal.cycles.map(cycle => ({
            name: cycle.name,
            appraisal_starts: cycle.appraisal_starts,
            appraisal_ends: cycle.appraisal_ends
          }));
        }
      } catch (error) {
        throw new Error(error);
      }
    },

    async loadPerformanceSettings() {
      try {
        const response = await this.$_getPerformanceGeneralSetting();
        this.managerLock = response.data.PerformanceSettings[0].managerCanLockAgreement;

        // Check if user has direct reports
        const directReportResponse = await this.$_getManagerDirectReport(this.$AuthUser?.id);
        this.hasDirectReport = directReportResponse.data.directReport.length > 0;
      } catch (error) {
        throw new Error(error);
      }
    },

    // KPA Methods
    getKPAWeight(kpaTypes, kpaTypesId) {
      if (kpaTypes.id === kpaTypesId) {
        return kpaTypes.kpas.reduce((sum, kpi) => sum + Number(kpi.weight), 0) || 0;
      }
      return 0;
    },

    getTotalWeight(kpaTypes, kpaTypesId) {
      const kpaWeight = this.kpaWeights.find(kpa => kpa.id === kpaTypesId);
      return kpaWeight ? kpaWeight.totalKpiWeight : 0;
    },

    updateKPAWeights() {
      this.templateKPATypes.forEach(kpaType => {
        const sum = this.getKPAWeight(kpaType);
        const kpaWeight = this.kpaWeights.find(k => k.id === kpaType.id);
        if (kpaWeight) {
          kpaWeight.weight = sum;
        }
      });
      this.calculateTotalAllocatedWeight();
    },

    // getTotalWeight(kpaType) {
    //   const kpaWeight = this.kpaWeights.find(k => k.id === kpaType.id);
    //   return kpaWeight ? kpaWeight.totalKpiWeight : 0;
    // },

    calculateTotalAllocatedWeight() {
      this.allocatedWeight = this.templateKPATypes.reduce((total, kpaType) => total + this.getKPAWeight(kpaType), 0);

      // Enable/disable submit button based on weight validation
      this.disableBtn = this.allocatedWeight !== 100 || !this.validateKPAs();
    },

    validateKPAs() {
      return this.templateKPATypes.every(kpaType => {
        // Check if kpas array exists and has items
        if (!kpaType.kpas || kpaType.kpas.length === 0) return false;

        return kpaType.kpas.every(kpa => {
          // Check KPA-level requirements
          const hasName = kpa.name && kpa.name.trim() !== '';
          const hasObjective = kpa.objective && kpa.objective.trim() !== '';
          const hasWeight = kpa.weight !== null && kpa.weight !== undefined;

          const kpiArray = kpa.kpi || kpa.kpis || []

          // Only validate the kpi array (ignore kpis array)
          const hasValidKPIs =
            kpiArray.length > 0 &&
            kpiArray.every(k => k.criteria && k.criteria.trim() !== '')

          return hasName && hasObjective && hasWeight && hasValidKPIs;
        });
      });
    },

    async createNewKPA(kpaType) {
      try {
        // Validate all fields in the current KPA type first
        const isValid = await this.validateKPAType(kpaType);

        if (!isValid) {
          this.$toasted.error("Please fill all required fields before adding a new KPI", { duration: 5000 });
          return;
        }

        if (this.allocatedWeight < 100) {
          kpaType.kpas.push(this.createEmptyKPA());
          this.updateKPAWeights();
        } else {
          this.$toasted.error("Total allocated weight cannot exceed 100%", { duration: 5000 });
        }
      } catch (error) {
        throw new Error(error);
      }
    },

    async validateKPAType(kpaType) {
      const allValid = Array.isArray(kpaType.kpas) && kpaType.kpas.every(kpa => {
        if (!kpa.name?.trim() || !kpa.objective?.trim() || !kpa.weight) {
          return false;
        }

        const kpis = Array.isArray(kpa.kpis) ? kpa.kpis : [];
        return kpis.every(criteria => criteria.criteria?.trim());
      });

      if (!allValid) {
        return false;
      }
      const observer = this.$refs[`kpa-observer-${kpaType.id}`]?.[0];
      if (observer) {
        const isValid = await observer.validate();
        if (!isValid) {
          return false;
        }
      }

      return true;
    },

    deleteKPA(index, kpaType) {
      if (kpaType.kpas.length > 1) {
        kpaType.kpas.splice(index, 1);
        this.updateKPAWeights(); // Add this line
      } else {
        this.$toasted.error(
          "Cannot delete the only KPI in a KPA Type",
          { duration: 5000 }
        );
      }
    },

    // Form Submission Methods
    async submit(status) {
      try {
        this.preparePayload(status);

        if (status !== 'draft') {
          if (!this.validateSubmission()) {
            return;
          }
        }

        const response = await this.$_createEmployeeAgreement(this.payload);
        if(!this.payload.isDraft) {
        this.$router.back();
        }
        else {
          this.getAgreements();
        }
        this.$toasted.success(response.data.message, { duration: 5000 });
      } catch (error) {
        this.handleSubmissionError(error);
      }
    },

    preparePayload(status) {
      let totalWeight = 0;

      this.payload.kpa_types = this.templateKPATypes.map(kpaType => {
        const newKPAs = kpaType.kpas.map(({ state, isEdit, ...kpa }) => {

          const weight = Number(kpa.weight) || 0;
          totalWeight += weight;



          return {
            ...kpa,
            kpis: kpa.kpis || [],
          };
        });

        return {
          kpa_types_id: kpaType.id,
          kpas: newKPAs
        };
      });

      this.payload.totalWeight = totalWeight;

      this.payload.status = status;
      this.payload.title = this.payload.description;
      this.payload.orgId = this.$orgId;
      this.payload.created_by = this.$AuthUser.id;
      this.payload.senderId = this.$AuthUser.id;
      this.payload.created_for = this.$AuthUser.id;

      if (status === "active") {
        this.payload.agreement_status = "shared";
      }
    },

    validateSubmission() {
      if (!this.payload.year) {
        this.periodError = true;
        this.$toasted.error("Please select a period/year");
        return false;
      }

      if (this.payload.totalWeight !== 100) {
        this.$toasted.error(`Total weight must be 100%. Current total is ${this.payload.totalWeight}%.`);
        return false;
      }

      return true;
    },

    handleSubmissionError(error) {
      const errorMsg = error.response?.data?.message || "Failed to submit agreement";
      this.$toasted.error(errorMsg, { duration: 5000 });

      // Reset year to Date object if submission failed
      if (typeof this.payload.year === "number") {
        this.payload.year = new Date(this.payload.year, 0);
      }

      this.disableBtn = false;
    },

    getAgreements() {
      this.$_getEmployeeAgreement(this.$AuthUser.id).then((response) => {
        const myAgreements = response.data.EmployeePerformanceAgreements;
        const agreement = myAgreements.find(a => Number(a.year) === this.payload.year);
        const agreementKPA = agreement.employee_agreement_kpa;

        this.payload.agreementId = agreement.id;
        this.templateKPATypes = this.templateKPATypes.map((kpaType) => ({
            ...kpaType,
            kpas: agreementKPA
              .filter(employeeKpi => employeeKpi.kpaTypeId === kpaType.id)
              .map(employeeKpi => ({
                state: true,
                isEdit: false,
                isLocked: employeeKpi.isLocked,
                name: employeeKpi.name,
                objective: employeeKpi.objective,
                kpaId: employeeKpi.id,
                kpis: this.getCriteriaData(employeeKpi.kpis_employee),
                weight: employeeKpi.weight,
              }))
          }));


      });
    },

    checkSelected(action) {
      if (this.validateKPAs()) {
        if (action === "Submit") {
          this.payload.share_for_review = false;
          this.submit("active", 'general');
        } else {
          this.payload.share_for_review = true;
          this.submit("active", 'general');
        }
      } else {
        this.$toasted.error("Please fill all required fields", { duration: 5000 });
      }
    },

    // Utility Methods
    periodSelect(data) {
      this.periodError = false;
      this.payload.year = data.getFullYear();
      const found = this.filteredTemplateOptions.find(template => template.year && template.year === this.payload.year);
      if (found) {
        this.selectedTemplate = found.id;
      }
    },

    handleOpen(state) {
      this.disableBtn = !state;
    },

    handleImport() {
      this.openImport = true;
    },

    closeImport() {
      this.openImport = false;
    },

    goBack() {
      this.$router.push({
        name: "EssAgrreements",
        query: { activeTab: "My Agreements" }
      });
    },

    cancel() {
      this.goBack();
    }
  }
};
</script>

<style>
.cloudenly-input {
  padding-right: 8px !important;
  padding-left: 8px !important;
  padding-top: 5px !important;
  padding-bottom: 5px !important;
  height: 38px !important;
}
</style>

<style scoped>
.date-input {
  width: 100%;
  outline: none;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.5rem;
}

.date-input:focus {
  border-color: #7c3aed;
  ring: 2px;
  ring-color: #7c3aed;
}

.border-red-500 {
  border-color: #ef4444;
}

.text-red-500 {
  color: #ef4444;
}

.bg-gray-100 {
  background-color: #f3f4f6;
}

.bg-purple-700 {
  background-color: #7c3aed;
}

.text-purple-700 {
  color: #7c3aed;
}

.border-purple-700 {
  border-color: #7c3aed;
}

.cursor-not-allowed {
  cursor: not-allowed;
}

.space-y-4>*+* {
  margin-top: 1rem;
}

.gap-3 {
  gap: 0.75rem;
}
</style>
